import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { AiMarketingService } from '@admin-core/services/ai-marketing.service';
import { MetaInstagramAsset, MetaOAuthIntent, MetaPageAsset } from '@admin-core/models/ai-marketing.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { TranslationKey } from '@admin-core/i18n/translations/en';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

const CONNECT_CTX_KEY = 'aiMarketing.connectCtx';

interface ConnectCtx {
  wantInstagram?: boolean;
  reconnect?: boolean;
}

@Component({
  selector: 'app-ai-marketing-connect',
  standalone: true,
  imports: [FormsModule, TranslatePipe],
  templateUrl: './ai-marketing-connect.component.html',
  styleUrl: './ai-marketing-admin.scss'
})
export class AiMarketingConnectComponent implements OnInit {
  readonly reconnect = input(false);
  /** Skip step 1 when Meta is already authorized (continue setup / OAuth callback). */
  readonly resumeAfterAuth = input(false);
  readonly completed = output<void>();
  readonly authorized = output<void>();

  private readonly api = inject(AiMarketingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly step = signal<1 | 2 | 3>(1);
  readonly busy = signal(false);
  readonly wantInstagram = signal(false);
  readonly pages = signal<MetaPageAsset[]>([]);
  readonly instagram = signal<MetaInstagramAsset[]>([]);
  readonly instagramOnPage = signal<MetaInstagramAsset[]>([]);
  readonly instagramOtherOnMeta = signal<MetaInstagramAsset[]>([]);
  readonly selectedPageId = signal('');
  readonly selectedIgId = signal('');

  ngOnInit(): void {
    this.restoreContext();

    const params = this.route.snapshot.queryParamMap;
    const error = params.get('meta_error');
    const connected = params.get('meta_connected');

    if (error) {
      void this.confirmDialog.alert({
        title: this.i18n.t('common.errorTitle'),
        message: error
      });
      this.clearOAuthQueryParams();
      return;
    }

    if (connected === '1' || this.resumeAfterAuth()) {
      this.busy.set(true);
      this.refreshAssets();
      return;
    }

    this.resetWizardState();
  }

  showInstagramPicker(): boolean {
    return this.wantInstagram();
  }

  oauthIntent(): MetaOAuthIntent {
    return {
      facebookPage: true,
      instagram: this.wantInstagram()
    };
  }

  permissionKeys(): TranslationKey[] {
    const intent = this.oauthIntent();
    const keys: TranslationKey[] = [];
    if (intent.facebookPage) {
      keys.push(
        'aiMarketing.connect.scope.pagesShowList',
        'aiMarketing.connect.scope.pagesReadEngagement',
        'aiMarketing.connect.scope.pagesManagePosts',
        'aiMarketing.connect.scope.businessManagement'
      );
    }
    if (intent.instagram) {
      keys.push(
        'aiMarketing.connect.scope.instagramBasic',
        'aiMarketing.connect.scope.instagramContentPublish',
        'aiMarketing.connect.scope.instagramManageInsights'
      );
    }
    return keys;
  }

  toggleWantInstagram(checked: boolean): void {
    this.wantInstagram.set(checked);
    if (!checked) {
      this.selectedIgId.set('');
    }
    this.persistContext();
  }

  goBack(): void {
    if (this.step() === 3 || this.step() === 2) {
      this.step.set(1);
    }
  }

  showBackButton(): boolean {
    return this.step() === 3 || this.step() === 2;
  }

  isConfigureNavActive(): boolean {
    return this.step() === 1;
  }

  isAccountsNavActive(): boolean {
    return this.step() === 2 || this.step() === 3;
  }

  isConfigureNavDone(): boolean {
    return this.step() >= 2;
  }

  startOAuth(): void {
    this.persistContext();
    this.busy.set(true);
    this.api.getOAuthUrl(this.oauthIntent()).subscribe({
      next: (result) => {
        this.redirectToMetaOAuth(result.url);
      },
      error: (err) => {
        this.busy.set(false);
        void this.confirmDialog.alert({
          title: this.i18n.t('common.errorTitle'),
          message: err?.error?.error ?? this.i18n.t('aiMarketing.loadError')
        });
      }
    });
  }

  refreshAssets(): void {
    this.busy.set(true);

    this.api.getPages('facebook').subscribe({
      next: (pages) => {
        this.pages.set(pages);
        if (pages.length === 0) {
          this.step.set(2);
          this.busy.set(false);
          this.finishOAuthReturn();
          return;
        }
        if (pages.length === 1) {
          this.selectedPageId.set(pages[0].id);
          if (this.showInstagramPicker()) {
            this.loadInstagramForPage(pages[0].id, true);
            return;
          }
        }
        this.step.set(3);
        this.busy.set(false);
        this.finishOAuthReturn();
      },
      error: (err) => this.handleAssetsError(err)
    });
  }

  onPageChange(): void {
    const pageId = this.selectedPageId();
    if (!pageId) {
      this.instagram.set([]);
      this.instagramOnPage.set([]);
      this.instagramOtherOnMeta.set([]);
      this.selectedIgId.set('');
      return;
    }
    if (this.showInstagramPicker()) {
      this.loadInstagramForPage(pageId);
    }
  }

  hasInstagramOptions(): boolean {
    return this.instagramOnPage().length + this.instagramOtherOnMeta().length > 0;
  }

  private loadInstagramForPage(pageId: string, autoSelectPage = false): void {
    forkJoin({
      onPage: this.api.getInstagram(pageId, 'facebook'),
      onMeta: this.api.getInstagram(undefined, 'facebook')
    }).subscribe({
      next: ({ onPage, onMeta }) => {
        const onPageIds = new Set(onPage.map((i) => i.id));
        const other = onMeta.filter((i) => !onPageIds.has(i.id));
        this.instagramOnPage.set(onPage);
        this.instagramOtherOnMeta.set(other);
        this.instagram.set([...onPage, ...other]);

        if (this.wantInstagram() && onPage.length === 1) {
          this.selectedIgId.set(onPage[0].id);
        } else if (this.wantInstagram() && onPage.length === 0 && other.length === 1) {
          this.selectedIgId.set(other[0].id);
        } else if (!autoSelectPage) {
          this.selectedIgId.set('');
        }

        this.clearOAuthQueryParams();
        this.step.set(3);
        this.busy.set(false);
        this.finishOAuthReturn();
      },
      error: () => {
        this.instagram.set([]);
        this.instagramOnPage.set([]);
        this.instagramOtherOnMeta.set([]);
        this.selectedIgId.set('');
        this.step.set(3);
        this.busy.set(false);
        this.finishOAuthReturn();
      }
    });
  }

  saveSelection(): void {
    const page = this.pages().find((p) => p.id === this.selectedPageId());
    if (!page) {
      void this.confirmDialog.alert({
        title: this.i18n.t('common.errorTitle'),
        message: this.i18n.t('aiMarketing.connect.selectRequired')
      });
      return;
    }

    this.busy.set(true);
    const igId = this.showInstagramPicker() ? this.selectedIgId() || null : null;
    this.api
      .connectFacebookPage({
        facebookPageId: page.id,
        facebookPageName: page.name,
        instagramAccountId: igId
      })
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.clearContext();
          this.completed.emit();
        },
        error: (err) => this.handleConnectError(err, () => this.saveFacebookWithOverwrite(page))
      });
  }

  private saveFacebookWithOverwrite(page: MetaPageAsset): void {
    this.busy.set(true);
    const igId = this.showInstagramPicker() ? this.selectedIgId() || null : null;
    this.api
      .connectFacebookPage({
        facebookPageId: page.id,
        facebookPageName: page.name,
        instagramAccountId: igId,
        confirmOverwrite: true
      })
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.clearContext();
          this.completed.emit();
        },
        error: (err) => this.fail(err)
      });
  }

  private resetWizardState(): void {
    this.step.set(1);
    this.busy.set(false);
    this.pages.set([]);
    this.instagram.set([]);
    this.instagramOnPage.set([]);
    this.instagramOtherOnMeta.set([]);
    this.selectedPageId.set('');
    this.selectedIgId.set('');
    this.wantInstagram.set(false);
  }

  private redirectToMetaOAuth(oauthUrl: string): void {
    window.location.assign(oauthUrl);
  }

  private persistContext(): void {
    const ctx: ConnectCtx = {
      wantInstagram: this.wantInstagram(),
      reconnect: this.reconnect()
    };
    sessionStorage.setItem(CONNECT_CTX_KEY, JSON.stringify(ctx));
  }

  private restoreContext(): void {
    try {
      const raw = sessionStorage.getItem(CONNECT_CTX_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw) as ConnectCtx;
      if (typeof ctx.wantInstagram === 'boolean') {
        this.wantInstagram.set(ctx.wantInstagram);
      }
    } catch {
      // ignore invalid session data
    }
  }

  private clearContext(): void {
    sessionStorage.removeItem(CONNECT_CTX_KEY);
  }

  private handleConnectError(
    err: {
      status?: number;
      error?: {
        error?: string;
        currentInstagram?: { username: string };
        currentFacebookPage?: { name: string };
      };
    },
    onOverwrite: () => void
  ): void {
    if (err.status === 409 && err.error?.currentInstagram) {
      this.busy.set(false);
      void this.confirmDialog
        .confirm({
          title: this.i18n.t('aiMarketing.connect.overwriteTitle'),
          message: this.i18n.t('aiMarketing.connect.overwriteMessage', {
            username: err.error.currentInstagram.username
          })
        })
        .then((ok) => {
          if (ok) onOverwrite();
        });
      return;
    }
    if (err.status === 409 && err.error?.currentFacebookPage) {
      this.busy.set(false);
      void this.confirmDialog
        .confirm({
          title: this.i18n.t('aiMarketing.connect.overwritePageTitle'),
          message: this.i18n.t('aiMarketing.connect.overwritePageMessage', {
            name: err.error.currentFacebookPage.name
          })
        })
        .then((ok) => {
          if (ok) onOverwrite();
        });
      return;
    }
    this.fail(err);
  }

  private clearOAuthQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }

  private finishOAuthReturn(): void {
    this.clearOAuthQueryParams();
    this.authorized.emit();
  }

  private handleAssetsError(err: { error?: { error?: string } }): void {
    const message = err?.error?.error ?? '';
    if (this.isConnectFirstError(message)) {
      this.step.set(1);
      this.busy.set(false);
      return;
    }
    this.fail(err);
  }

  private isConnectFirstError(message: string): boolean {
    return /connect facebook first|not authorized|connect instagram|connect meta|not authorized for/i.test(
      message
    );
  }

  private fail(err: { error?: { error?: string } }): void {
    this.busy.set(false);
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message: err?.error?.error ?? this.i18n.t('aiMarketing.loadError')
    });
  }
}
